'use client'

import {
  Box,
  Container,
  Fade,
  keyframes,
  Stack,
  Typography,
} from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RoundButton from '../kokokara/ui/button/RoundButton'
import UnderlineButton from '../kokokara/ui/button/UnderlineButton'

const WALK_MS = 5000
const walkAcross = keyframes`	
0% { transform: translateX(100vw); opacity: 0; }	
10% { opacity: 1; }	
90% { opacity: 1; }	
100% { transform: translateX(-200px); opacity: 0; }	
`

export default function Introduction() {
  const { t } = useTranslation()
  const router = useRouter()

  const [introProgress, setIntroProgress] = useState(0)

  // 歩行
  const [showWalker, setShowWalker] = useState(false)
  const [walkFrame, setWalkFrame] = useState<0 | 1>(0)
  const frameTimerRef = useRef<number | null>(null)

  const timersRef = useRef<number[]>([])
  const intro1Ref = useRef<HTMLDivElement>(null)
  const intro2Ref = useRef<HTMLDivElement>(null)
  const intro3Ref = useRef<HTMLDivElement>(null)

  const scrollToEl = (
    el: HTMLElement | null,
    block: ScrollLogicalPosition = 'end',
  ) => {
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block })
    })
  }

  const startWalk = () => {
    setShowWalker(true)
    frameTimerRef.current = window.setInterval(() => {
      setWalkFrame((f) => (f === 0 ? 1 : 0))
    }, 180)
  }

  // 表示スケジュール
  useEffect(() => {
    const addTimer = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms)
      timersRef.current.push(id)
    }
    const lead = 150 // 文章表示150ms前に先行スクロール
    addTimer(() => setIntroProgress(1), 800)
    addTimer(() => scrollToEl(intro1Ref.current), 3000 - lead)
    addTimer(() => setIntroProgress(2), 3000)
    addTimer(() => scrollToEl(intro2Ref.current), 9000 - lead)
    addTimer(() => setIntroProgress(3), 9000)
    addTimer(() => scrollToEl(intro3Ref.current), 15000 - lead)
    addTimer(() => setIntroProgress(4), 15000)
    addTimer(() => setIntroProgress(5), 20000)
    addTimer(() => setIntroProgress(6), 21000)
    addTimer(startWalk, 21800)

    return () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
      if (frameTimerRef.current) {
        clearInterval(frameTimerRef.current)
        frameTimerRef.current = null
      }
    }
  }, [])

  const handleSkip = () => {
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
    setIntroProgress(6)
    setTimeout(() => scrollToEl(intro3Ref.current), 0)
    setTimeout(startWalk, 800)
  }

  const handleClick = () => {
    router.push('/kokokara')
  }

  const introItems = [
    {
      i18nKey: 'home.intro1',
      id: 'intro1',
      ref: intro1Ref,
      show: introProgress >= 2,
      timeout: 1800,
    },
    {
      i18nKey: 'home.intro2',
      id: 'intro2',
      ref: intro2Ref,
      show: introProgress >= 3,
      timeout: 1800,
    },
    {
      i18nKey: 'home.intro3',
      id: 'intro3',
      ref: intro3Ref,
      show: introProgress >= 4,
      timeout: 1800,
    },
  ]

  return (
    <Container
      sx={{
        alignItems: 'center',
        backgroundColor: '#EFF7EA',
        backgroundImage: 'url("/yomogi_bg.svg")',
        backgroundPosition: 'calc(50% + 28px) -248px',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '175%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '100vh',
        p: '20px',
      }}
    >
      {/* スキップボタン */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mr: 1,
          width: '100%',
        }}
      >
        <Fade in={introProgress >= 1} timeout={600}>
          <UnderlineButton onClick={handleSkip}>
            {t('home.skip')}
          </UnderlineButton>
        </Fade>
      </Box>

      {/* 本文カード */}
      <Box
        sx={{
          WebkitOverflowScrolling: 'touch',
          backgroundColor: 'rgba(255,255,255,0.8)',
          borderRadius: '40px 4px 40px 4px',
          boxSizing: 'border-box',
          height: 240,
          mb: 2,
          overflowY: 'auto',
          p: '30px',
          scrollBehavior: 'smooth',
          width: '100%',
        }}
      >
        <Stack spacing={2}>
          <Fade in={introProgress >= 1} timeout={1800}>
            <Box>
              <Typography sx={{ fontSize: 15 }}>{t('home.welcome')}</Typography>
            </Box>
          </Fade>
          {introItems.map(({ id, show, timeout, ref, i18nKey }) => (
            <Fade
              key={id}
              in={show}
              timeout={timeout}
              onEntered={() => scrollToEl(ref.current)}
            >
              <Box ref={ref}>
                <Typography
                  sx={{
                    fontSize: 15,
                    lineHeight: '30px',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {t(i18nKey)}
                </Typography>
              </Box>
            </Fade>
          ))}
        </Stack>
      </Box>

      {/* 養生の旅に出るボタン */}
      <Fade in={introProgress >= 5} timeout={600}>
        <RoundButton fullWidth variant="contained" onClick={handleClick}>
          {t('toJourney')}
        </RoundButton>
      </Fade>

      {/* yojyoくん画像 */}
      <Fade in={introProgress <= 4} timeout={600}>
        <Box sx={{ mt: '8px' }}>
          <img
            src="/yojyokun-front.png"
            width="200"
            height="200"
            alt="yojyokun-front"
            loading="lazy"
          />
        </Box>
      </Fade>

      {/* yojyoくん歩行 */}
      {showWalker && (
        <Box
          sx={{
            alignItems: 'center',
            animation: `${walkAcross} ${WALK_MS}ms ease-in-out infinite`,
            bottom: 16,
            display: 'flex',
            height: 200,
            justifyContent: 'center',
            left: 0,
            position: 'fixed',
            width: 200,
          }}
        >
          <img
            src={
              walkFrame === 0 ? '/yojyokun-left-1.png' : '/yojyokun-left-2.png'
            }
            width={180}
            height={180}
            alt="yojyokun-walking"
            loading="eager"
            style={{ display: 'block' }}
          />
        </Box>
      )}
    </Container>
  )
}
